import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarBlank, GearSix, Power, Broadcast, Export } from "@phosphor-icons/react";
import { ExportDialog } from "../components/ExportDialog";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../lib/realtime";
import { useDepartments } from "../components/DepartmentPicker";
import { DayBanner, ShiftTabs, RunCard } from "../components/checklists/FloorWidgets";
import { RunPanel } from "../components/checklists/RunPanel";
import { TemplatesView } from "../components/checklists/TemplatesView";
import { fmtDay, fmtTime, inputCls, btnGhost } from "../components/checklists/shared";

export default function ChecklistsPage() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const departments = useDepartments();
  const [venues, setVenues] = useState([]);
  const [config, setConfig] = useState(null);
  const [venueId, setVenueId] = useState(sp.get("venue_id") || "");
  const [exporting, setExporting] = useState(false);
  const [deptId, setDeptId] = useState(sp.get("department_id") || "");
  const [data, setData] = useState(null);
  const [shift, setShift] = useState("");
  const [date, setDate] = useState("");
  const [activeRun, setActiveRun] = useState(null);
  const [queue, setQueue] = useState([]);
  const [view, setView] = useState("floor");
  const [closing, setClosing] = useState(false);
  const pickedShift = useRef(false);

  const isArea = ["cluster", "regional"].includes(user.role);
  const isTop = ["admin", "gm"].includes(user.role) || user.is_master_operator || isArea;
  const isManager = config ? config.can_verify : ["admin", "gm", "cluster", "regional", "manager"].includes(user.role);
  const canClose = config ? config.can_close_shift : ["admin", "gm", "manager", "supervisor"].includes(user.role);
  const myDepts = user.department_ids || [];
  const scopedManager = user.role === "manager" && myDepts.length > 0 && !isTop;
  const unscopedManager = user.role === "manager" && myDepts.length === 0 && !isTop;
  const showScope = isTop || scopedManager || unscopedManager;
  const live = useRealtime((msg) => {
    if (["checklist.run", "shift.state", "checklist.template", "checklist.config"].includes(msg.type)) scheduleReload();
  });

  useEffect(() => {
    api.get("/checklists/config").then((r) => setConfig(r.data)).catch(() => {});
    if (["admin", "gm", "cluster", "regional", "manager"].includes(user.role)) api.get("/venues").then((r) => setVenues(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!(isTop || unscopedManager) || venueId || !venues.length) return;
    const own = (user.venue_ids || [])[0] || user.venue_id;
    setVenueId(venues.find((v) => v.id === own)?.id || venues[0].id);
  }, [venues, isTop]); // eslint-disable-line react-hooks/exhaustive-deps

  const deptOptions = departments.filter((d) => d.active !== false && (scopedManager ? myDepts.includes(d.id) : (!venueId || !d.venue_id || d.venue_id === venueId)));
  useEffect(() => {
    if (!showScope || !deptOptions.length) return;
    if (!deptId || !deptOptions.some((d) => d.id === deptId)) setDeptId(deptOptions[0].id);
  }, [departments, venueId, showScope]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = showScope ? { venue_id: isTop ? venueId : "", department_id: deptId } : {};
  const ready = !showScope || !!deptId;

  const load = useCallback(() => {
    if (!ready) return;
    api.get("/checklists/runs", { params: { ...params, date } })
      .then((r) => {
        setData(r.data);
        if (!pickedShift.current) setShift((s) => s || r.data.state.current_shift);
      })
      .catch((e) => toast.error(apiError(e)));
    if (isManager) api.get("/checklists/queue", { params }).then((r) => setQueue(r.data)).catch(() => {});
  }, [ready, venueId, deptId, date, config]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const reloadTimer = useRef(null);
  const scheduleReload = () => { clearTimeout(reloadTimer.current); reloadTimer.current = setTimeout(() => load(), 250); };

  const closeShift = async () => {
    if (!window.confirm(`Close the operating day ${fmtDay(data.state.operating_date)} for this department? Open checklists stay on record and the date rolls forward.`)) return;
    setClosing(true);
    try {
      const { data: res } = await api.post("/shift-state/close", params);
      toast.success(`Closed ${fmtDay(res.closed_date)} — now operating ${fmtDay(res.state.operating_date)}`);
      pickedShift.current = false;
      setShift("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setClosing(false); }
  };

  if (view === "templates") return <TemplatesView config={config} departments={departments} venues={venues} venueId={venueId} onBack={() => { setView("floor"); load(); }} />;

  const runs = data?.runs || [];
  const shifts = data?.state?.shifts || config?.shifts || [];
  const shownRuns = runs.filter((r) => r.shift === shift);
  const isHistory = !!data?.history;

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="checklists-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400 flex items-center gap-2">
            Operational checklists & logs
            <span data-testid="realtime-status" className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] tracking-normal px-2 py-0.5 rounded border ${live === "live" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-400 bg-slate-50 border-slate-200"}`}>
              <Broadcast size={12} weight={live === "live" ? "fill" : "regular"} /> {live === "live" ? "Live sync" : "Reconnecting"}
            </span>
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Checklists</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 h-11">
            <CalendarBlank size={18} className="text-slate-400" />
            <input data-testid="checklist-date-picker" type="date" value={date} max={data?.state?.operating_date} onChange={(e) => setDate(e.target.value === data?.state?.operating_date ? "" : e.target.value)} className="bg-transparent text-sm font-semibold outline-none" />
            {date && <button data-testid="checklist-date-clear" onClick={() => setDate("")} className="text-xs font-bold text-slate-500 hover:text-slate-900">Today</button>}
          </div>
          {isManager && <button data-testid="export-button" onClick={() => setExporting(true)} className={`${btnGhost} flex items-center gap-1.5`}><Export size={16} /> Export</button>}
          {(config?.can_manage_templates || config?.can_manage_local_lines) && (
            <button data-testid="templates-button" onClick={() => setView("templates")} className={`${btnGhost} flex items-center gap-1.5`}><GearSix size={16} /> Templates</button>
          )}
          {canClose && !isHistory && data && (
            <button data-testid="close-shift-button" onClick={closeShift} disabled={closing} className="h-11 px-5 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40 flex items-center gap-1.5 tap-press">
              <Power size={16} weight="bold" /> {closing ? "Closing…" : "Close shift"}
            </button>
          )}
        </div>
      </div>

      {showScope && (
        <div className="flex flex-col sm:flex-row gap-2" data-testid="checklist-scope">
          {isTop && (
            <select data-testid="checklist-venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)} className={`${inputCls} sm:w-72 font-semibold`}>
              {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          )}
          <select data-testid="checklist-dept-select" value={deptId} onChange={(e) => setDeptId(e.target.value)} className={`${inputCls} sm:w-72 font-semibold`}>
            {deptOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      )}

      <DayBanner state={data?.state} historyDate={isHistory ? data.date : ""} />

      {data && <ShiftTabs shifts={shifts} runs={runs} value={shift} current={data.state.current_shift} onChange={(k) => { pickedShift.current = true; setShift(k); }} />}

      <div className="grid sm:grid-cols-2 gap-4" data-testid="run-list">
        {shownRuns.map((r) => <RunCard key={r.id} run={r} config={config} onOpen={setActiveRun} />)}
        {data && shownRuns.length === 0 && (
          <p className="text-slate-400 sm:col-span-2 bg-white border border-dashed border-slate-200 rounded-2xl p-8 text-center" data-testid="run-list-empty">
            {isHistory ? "No checklist records for this shift on that day." : "No checklists scheduled for this shift in this department."}
          </p>
        )}
      </div>

      {isManager && queue.length > 0 && !isHistory && (
        <section data-testid="verification-queue" className="bg-amber-50 border border-amber-200 rounded-2xl p-5 sm:p-6">
          <h2 className="font-heading text-lg font-bold text-amber-900">Awaiting verification ({queue.length})</h2>
          <div className="mt-3 divide-y divide-amber-200">
            {queue.map((r) => (
              <button key={r.id} data-testid={`queue-run-${r.id}`} onClick={() => setActiveRun(r)} className="w-full text-left py-3 flex items-center justify-between gap-3 hover:bg-amber-100/60 rounded-lg px-2">
                <div>
                  <p className="font-bold text-sm">{r.template_name} <span className="text-amber-700 font-semibold">· {r.shift} · {fmtDay(r.operating_date)}</span></p>
                  <p className="text-xs text-amber-800">Submitted by {r.submitted_by} at {fmtTime(r.submitted_at, data?.state?.timezone)}{r.progress.failed ? ` · ${r.progress.failed} exception(s)` : ""}</p>
                </div>
                <span className="text-xs font-bold text-amber-900">Review →</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {activeRun && <RunPanel runId={activeRun.id} shifts={shifts} tz={data?.state?.timezone} canVerify={isManager} readOnly={isHistory} onClose={() => setActiveRun(null)} onChanged={load} />}
      {exporting && <ExportDialog venues={venues} defaultVenueId={venueId} defaultDeptId={deptId} onClose={() => setExporting(false)} />}
    </div>
  );
}
